import { useLazyQuery } from "@apollo/client";
import React, { useState, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { useDebounce } from "../../../hooks/useDebounce";
import { PRODUCTS } from "../products.queries";
import {
  ProductsQuery,
  ProductsQueryVariables,
} from "../../../__generated__/ProductsQuery";
import { useLazyProduct } from "../hooks/useProduct";

interface IProductInput {
  form: UseFormReturn<any, any>;
  disabled?: boolean;
  error?: string;
}

export const ProductInput: React.FC<IProductInput> = ({
  form,
  disabled = false,
  error = null,
}) => {
  const limit = 25;
  const [isOpened, setIsOpened] = useState(false);
  const [searchFn, { data, loading, called, fetchMore }] = useLazyQuery<
    ProductsQuery,
    ProductsQueryVariables
  >(PRODUCTS);
  const [lazyProduct] = useLazyProduct();

  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [hasBeenSelected, setSelected] = useState(false);

  const productId = form.watch("productId");

  useEffect(() => {
    const fetchData = async (id) => {
      const { data: productData } = await lazyProduct({
        variables: { id: +id },
      });
      if (productData?.product?.result) {
        setIsOpened(false);
        setSelected(true);
        setSearch(productData?.product?.result?.name);
        setSelectedProduct(productData?.product?.result);
        form.setValue("productId", id);
      }
    };

    if (productId) {
      fetchData(productId);
    } else {
      setSearch("");
      setSelectedProduct(null);
    }
  }, [productId]);

  const [displayedSearch, setSearch] = useState("");
  const debouncedSearchTerm = useDebounce(displayedSearch, 500);

  useEffect(
    () => {
      if (debouncedSearchTerm) {
        setIsOpened(true);
        searchFn({
          variables: {
            limit,
            offset: 0,
            where: {
              search: displayedSearch,
            },
          },
        });
      }
    },
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );

  useEffect(() => {
    if (!hasBeenSelected) form.setValue("productId", null);
  }, [hasBeenSelected]);

  return (
    <div className="flex flex-col px-1">
      <label className="label">Produit</label>
      <input
        disabled={disabled}
        className="input"
        onChange={(e) => {
          setSearch(e.target.value);
          setSelected(false);
          form.setValue("productId", null);
        }}
        value={displayedSearch}
      />
      {!productId && error ? (
        <p className="error-message mb-2">{error}</p>
      ) : null}
      {isOpened && displayedSearch !== "" && !hasBeenSelected && (
        <>
          <label className="label">Résultats</label>
          <div className="input mb-2 ">
            {loading && <h2>Chargement ...</h2>}

            {called && data?.products?.results?.length == 0 && (
              <>
                <h2>Aucun resultat</h2>
              </>
            )}

            {data?.products?.results?.map((product) => (
              <div
                className="mb-2 cursor-pointer"
                key={`product-${product.id}`}
                onClick={() => {
                  setIsOpened(false);
                  setSearch(product.name);
                  setSelected(true);
                  setSelectedProduct(product);
                  form.setValue("productId", product.id);
                }}
              >
                <h2>{product.name}</h2>
              </div>
            ))}

            {data?.products.hasMore && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  fetchMore({
                    variables: {
                      where: {
                        search: displayedSearch,
                      },
                      limit,
                      offset: data?.products?.results?.length,
                    },
                  });
                }}
                disabled={loading}
                className={`self-center text-lg font-medium focus:outline-none text-white py-1 px-3 transition-colors ${
                  !loading
                    ? "bg-gray-700 hover:bg-gray-800"
                    : "bg-gray-300 pointer-events-none "
                }`}
              >
                {loading ? "Chargement" : "Plus de resultats"}{" "}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
